import React from "react";
import { createRoot } from "react-dom/client";

import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";

import "@fontsource/sora/300.css";
import "@fontsource/sora/400.css";
import "@fontsource/sora/500.css";
import "@fontsource/sora/600.css";
import "@fontsource/sora/700.css";

import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/500.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/playfair-display/700.css";

import "./index.css";

type Root = ReturnType<typeof createRoot>;

const renderConfigError = (root: Root, error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  root.render(
    <React.StrictMode>
      <div style={{ maxWidth: 720, margin: "40px auto", padding: "0 16px", fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Konfigurace aplikace chybí</h1>
        <p style={{ marginBottom: 12 }}>
          Aplikace potřebuje Supabase konfiguraci ve <code>VITE_*</code> proměnných už při buildu.
        </p>
        <ul style={{ marginBottom: 12, paddingLeft: 18 }}>
          <li>
            <code>VITE_SUPABASE_URL</code>
          </li>
          <li>
            <code>VITE_SUPABASE_PUBLISHABLE_KEY</code> (nebo <code>VITE_SUPABASE_ANON_KEY</code>)
          </li>
        </ul>
        <p style={{ marginBottom: 12 }}>
          Pro Lovable je nastav v projektovém nastavení jako Environment Variables. Pro Cloudflare musí být dostupné v build prostředí
          (ne jen runtime secrets).
        </p>
        <details style={{ opacity: 0.8 }}>
          <summary>Technický detail</summary>
          <pre style={{ whiteSpace: "pre-wrap" }}>{message}</pre>
        </details>
      </div>
    </React.StrictMode>
  );
};

const rootEl = document.getElementById("root");

if (rootEl) {
  const root = createRoot(rootEl);

  void (async () => {
    try {
      const [{ initI18n }, { default: App }, { SessionProvider }, { PhiModeProvider }] = await Promise.all([
        import("./i18n"),
        import("./App.tsx"),
        import("./hooks/useSession"),
        import("./hooks/usePhiMode"),
      ]);

      await initI18n();

      root.render(
        <React.StrictMode>
          <SessionProvider>
            <PhiModeProvider>
              <App />
            </PhiModeProvider>
          </SessionProvider>
        </React.StrictMode>
      );
    } catch (error) {
      renderConfigError(root, error);
    }
  })();
}
